import React from 'react'
import { msgid, ngettext, t } from 'ttag'

import { CandidateManagementModal } from 'components/modals/CandidateManagementModal/CandidateManagementModal'
import { DeleteCandidateModal } from 'components/modals/DeleteCandidateModal/DeleteCandidateModal'
import { MoveCandidateModal } from 'components/modals/MoveCandidateModal/MoveCandidateModal'
import { DeleteCandidateGroupModal } from 'components/modals/DeleteCandidateGroupModal/DeleteCandidateGroupModal'
import { ExportCandidateGroupModal } from 'components/modals/ExportCandidateGroupModal/ExportCandidateGroupModal'
import { MoveCandidateGroupModal } from 'components/modals/MoveCandidateGroupModal/MoveCandidateGroupModal'
import { SendTestsInvitationsModal } from 'components/modals/SendTestsInvitationsModal/SendTestsInvitationsModal'
import { SendTestsRemindersModal } from 'components/modals/SendTestsRemindersModal/SendTestsRemindersModal'
import { ActionsDropdown } from 'components/shared/ActionsDropdown/ActionsDropdown'
import { Button } from 'components/shared/Button/Button'
import { Link } from 'components/shared/Link/Link'
import { Pane } from 'components/shared/Pane/Pane'
import { SearchWidget } from 'components/shared/SearchWidget/SearchWidget'
import { InvitesTable } from './common/InvitesTable/InvitesTable'
import { FilterDropdown } from 'components/shared/FilterDropdown/FilterDropdown'
import { getFiltersConfigs } from '../model'
import { DEFAULT_MESSAGE_TEMPLATES_QUERY_PARAMS } from 'constants/templates'
import { toTemplates } from 'ducks/router'
import { useJobInvitesTab } from './hooks'
import { willExtendDeadline } from 'utils/sending'
import { PaneTitle } from '../PaneTitle/PaneTitle'
import { MailIcon } from 'components/shared/Icon/model'
import { FeatureFlag } from 'components/auth/FeatureFlag'
import styles from './Invites.module.scss'

export const Invites = ({ activeTab, project, hasInterviewModule }) => {
  const { id, name, active, department } = project ?? {}

  const { state, handlers } = useJobInvitesTab({ projectId: id })

  const {
    actions,
    candidate,
    count,
    invitees,
    deadline,
    remindees,
    expired,
    search,
    filter,
    hasMoveCandidatesFeature,
    isAtsLocked,
    isCandidateManagementModalOpen,
    isDeleteCandidateModalOpen,
    isMoveCandidateModalOpen,
    isDeleteCandidateGroupModalOpen,
    isExportCandidateGroupModalOpen,
    isMoveCandidateGroupModalOpen,
    isSendTestsInvitationsModalOpen,
    isSendTestsRemindersModalOpen,
    isNoInvitees,
    isNoRemindees,
    isNoExpired,
    isTargetingExpired,
  } = state
  const {
    getActions,
    handleFavorite,
    handleCandidateManagementModalClose,
    handleCandidateManagementModalConfirm,
    handleDeleteCandidateModalClose,
    handleDeleteCandidateModalConfirm,
    handleMoveCandidateModalClose,
    handleMoveCandidateModalConfirm,
    handleDeleteCandidateGroupModalClose,
    handleDeleteCandidateGroupModalConfirm,
    handleExportCandidateGroupModalClose,
    handleMoveCandidateGroupModalClose,
    handleMoveCandidateGroupModalConfirm,
    handleSendTestsInvitationsModalClose,
    handleSendTestsInvitationsModalConfirm,
    handleSendTestsInvitationsModalOpen,
    handleSendTestsRemindersModalClose,
    handleSendTestsRemindersModalConfirm,
    handleSendRemindersOpen,
    handleSendExpiredOpen,
    handleSendTestsMessage,
    setFilter,
    setSearch,
  } = handlers

  const remindersRecipients = candidate
    ? [candidate]
    : isTargetingExpired
      ? expired
      : remindees

  return (
    <>
      <Pane.Header
        direction="column"
        subtitle={`${count} ${t`candidates`}`}
        title={<PaneTitle label={name} active={active} />}
      >
        <SearchWidget query={search} onChange={setSearch} />
        <FilterDropdown
          size="small"
          filter={filter}
          setFilter={setFilter}
          filtersConfig={getFiltersConfigs(['is_favourite'])}
        />
        <Button
          className={styles.button}
          color="primary"
          icon={MailIcon}
          label={t`Send invitations`}
          size="small"
          variant="filled"
          onClick={handleSendTestsInvitationsModalOpen}
          isDisabled={isNoInvitees || !active}
          isLocked={isAtsLocked}
        />
        <Button
          className={styles.button}
          icon={MailIcon}
          label={t`Send reminders`}
          size="small"
          variant="outlined"
          onClick={handleSendRemindersOpen}
          isDisabled={isNoRemindees}
          isLocked={isAtsLocked}
        />
        {isNoExpired ? null : (
          <Button
            className={styles.button}
            icon={MailIcon}
            label={ngettext(
              msgid`Send to ${expired.length} past deadline`,
              `Send to ${expired.length} past deadline`,
              expired.length,
            )}
            size="small"
            variant="outlined"
            onClick={handleSendExpiredOpen}
            isLocked={isAtsLocked}
          />
        )}
        <FeatureFlag accountAdmin>
          <Link
            className={styles.button}
            label={t`Edit templates`}
            size="small"
            variant="outlined"
            to={toTemplates('messages', null, {
              ...DEFAULT_MESSAGE_TEMPLATES_QUERY_PARAMS,
              department: department?.id,
            })}
          />
        </FeatureFlag>
        <ActionsDropdown
          anchorClassName={styles.button}
          size="small"
          isDisabled={!active}
          {...{ actions }}
        />
      </Pane.Header>
      <InvitesTable
        projectId={id}
        tab={activeTab}
        onSendTestsMessage={handleSendTestsMessage}
        {...{
          search,
          filter,
          setFilter,
          getActions,
          hasInterviewModule,
          isAtsLocked,
          handleFavorite,
          active,
        }}
      />
      {hasMoveCandidatesFeature ? (
        <MoveCandidateGroupModal
          onCancel={handleMoveCandidateGroupModalClose}
          onClose={handleMoveCandidateGroupModalClose}
          onConfirm={handleMoveCandidateGroupModalConfirm}
          isOpen={isMoveCandidateGroupModalOpen}
        />
      ) : null}
      <ExportCandidateGroupModal
        onCancel={handleExportCandidateGroupModalClose}
        onClose={handleExportCandidateGroupModalClose}
        isOpen={isExportCandidateGroupModalOpen}
      />
      <DeleteCandidateGroupModal
        onCancel={handleDeleteCandidateGroupModalClose}
        onClose={handleDeleteCandidateGroupModalClose}
        onConfirm={handleDeleteCandidateGroupModalConfirm}
        isOpen={isDeleteCandidateGroupModalOpen}
      />
      <CandidateManagementModal
        onCancel={handleCandidateManagementModalClose}
        onClose={handleCandidateManagementModalClose}
        onConfirm={handleCandidateManagementModalConfirm}
        isOpen={isCandidateManagementModalOpen}
        {...{ candidate }}
      />
      {hasMoveCandidatesFeature ? (
        <MoveCandidateModal
          name={candidate?.name}
          onCancel={handleMoveCandidateModalClose}
          onClose={handleMoveCandidateModalClose}
          onConfirm={handleMoveCandidateModalConfirm}
          isOpen={isMoveCandidateModalOpen}
        />
      ) : null}
      <DeleteCandidateModal
        name={candidate?.name}
        onCancel={handleDeleteCandidateModalClose}
        onClose={handleDeleteCandidateModalClose}
        onConfirm={handleDeleteCandidateModalConfirm}
        isOpen={isDeleteCandidateModalOpen}
      />
      <SendTestsInvitationsModal
        candidates={candidate ? [candidate] : invitees}
        onCancel={handleSendTestsInvitationsModalClose}
        onClose={handleSendTestsInvitationsModalClose}
        onConfirm={handleSendTestsInvitationsModalConfirm}
        isOpen={isSendTestsInvitationsModalOpen}
        mode="job"
        {...{ deadline, name }}
      />
      <SendTestsRemindersModal
        candidates={remindersRecipients}
        isExtending={willExtendDeadline(remindersRecipients)}
        onCancel={handleSendTestsRemindersModalClose}
        onClose={handleSendTestsRemindersModalClose}
        onConfirm={handleSendTestsRemindersModalConfirm}
        isOpen={isSendTestsRemindersModalOpen}
        mode="job"
        {...{ deadline, name }}
      />
    </>
  )
}
